import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * PremiumBadge \u2014 Pill-shaped status indicator.
 *
 * Uses the token-driven soft palette + strong text tokens (WCAG AA safe).
 * Preserves sentence-case rule per \u00a77 (badge standard).
 */

type Variant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary';
type Size = 'sm' | 'md';

const VARIANT_STYLES: Record<Variant, string> = {
  success: 'bg-success-soft text-success-strong',
  warning: 'bg-warning-soft text-warning-strong',
  error:   'bg-destructive-soft text-destructive-strong',
  info:    'bg-info-soft text-info-strong',
  primary: 'bg-primary-soft text-primary-strong',
  neutral: 'bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300',
};

const SIZE_STYLES: Record<Size, string> = {
  sm: 'text-[11px] px-1.5 py-[1px]',
  md: 'text-[11px] px-2 py-0.5',
};

export interface PremiumBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  /** Render a subtle outlined border instead of filled pill */
  outlined?: boolean;
}

export const PremiumBadge = React.forwardRef<HTMLSpanElement, PremiumBadgeProps>(
  (
    {
      variant = 'neutral',
      size = 'md',
      icon,
      outlined = false,
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium leading-none whitespace-nowrap',
        VARIANT_STYLES[variant],
        SIZE_STYLES[size],
        outlined && 'bg-transparent border border-border-hairline',
        className,
      )}
      {...props}
    >
      {icon && <span className="flex-shrink-0 [&>svg]:w-3 [&>svg]:h-3">{icon}</span>}
      {children}
    </span>
  ),
);
PremiumBadge.displayName = 'PremiumBadge';
